import { isDefined } from "~/shared/common";
import { IconKind, PageKind, SkillKind } from "~/shared/enums";
import type { Skill, SkillUpdate } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { skillComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToSkills: Map<string, Skill>;
  _sortedSkillDuids: string[];
};

export const state: State = {
  _duidsToSkills: new Map(),
  _sortedSkillDuids: [],
};

export type Getters = {
  skillList: (options?: { includeHidden?: boolean }) => Skill[];
  getSkillByDuid: (duid: string) => Skill | undefined;
};

export type Actions = {
  createSkill: (
    title: string,
    order: string,
    partialSkill?: Partial<Skill>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Skill | undefined>;
  updateSkill: (
    update: SkillUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Skill | undefined>;
  deleteSkill: (skill: Skill, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setSkills: (skills: Skill[]) => void;
  $createOrUpdateSkill: (skill: Skill) => void;
  $deleteSkill: (skill: Skill) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  skillList() {
    return (options = {}) => {
      const { includeHidden = false } = options;
      const skills = this._sortedSkillDuids.map((e) => this._duidsToSkills.get(e)).filter(isDefined);
      return includeHidden ? skills : skills.filter((e) => !e.hidden);
    };
  },
  getSkillByDuid() {
    return (duid) => this._duidsToSkills.get(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createSkill(title, order, partialSkill = {}, options = {}) {
    const { awaitBackend = false } = options;
    const skill: Skill = {
      pageKind: PageKind.SKILL,
      duid: makeDuid(),
      kind: SkillKind.SKILL,
      title,
      order,
      prompt: makeEmptyLexicalState(),
      iconKind: IconKind.NONE,
      iconNameOrEmoji: "",
      colorHex: makeRandomColorHex(),
      hidden: false,
      ...partialSkill,
    };

    this.$createOrUpdateSkill(skill);

    const backendAction = this.$backend.skill.create(skill);
    if (awaitBackend) {
      await backendAction;
    }
    return skill;
  },
  async updateSkill(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const skill = this.getSkillByDuid(update.duid);
    if (!skill) {
      return undefined;
    }

    Object.assign(skill, update);
    updateInSorted(this._duidsToSkills, this._sortedSkillDuids, skill.duid, skillComparator);
    if (noBackend) {
      return skill;
    }

    const backendAction = this.$backend.skill.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return skill;
  },

  async deleteSkill(skill, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteSkill(skill);

    const backendAction = this.$backend.skill.delete(skill.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setSkills(skills) {
    setObjectMapAndSorted(
      this._duidsToSkills,
      this._sortedSkillDuids,
      skills.map((e) => ({ ...e, pageKind: PageKind.SKILL, kind: SkillKind.SKILL }) satisfies Skill),
      skillComparator
    );
  },
  $createOrUpdateSkill(skill) {
    const currentSkill = this.getSkillByDuid(skill.duid);

    // eslint-disable-next-line no-param-reassign
    skill.pageKind = PageKind.SKILL;
    // eslint-disable-next-line no-param-reassign
    skill.kind = SkillKind.SKILL;

    if (!currentSkill) {
      this._duidsToSkills.set(skill.duid, skill);
    } else {
      Object.assign(currentSkill, skill);
    }
    updateInSorted(this._duidsToSkills, this._sortedSkillDuids, skill.duid, skillComparator);
  },
  $deleteSkill(skill) {
    this._duidsToSkills.delete(skill.duid);
    removeFromSorted(this._sortedSkillDuids, skill.duid);
  },
};

export { actions, getters };
